import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  ClipboardList,
  User,
  CreditCard,
  Truck,
  Users,
  ChefHat,
  ShieldCheck,
  Lock,
  AlertTriangle,
  RotateCcw,
  Mail,
  ChevronRight,
  Headphones,
} from 'lucide-react';

const SECTIONS = [
  {
    slug: 'introduction',
    title: 'Introduction',
    icon: FileText,
    content:
      'Welcome to Panda On Wheels. These Terms & Conditions ("Terms") govern your access to and use of the Panda On Wheels website, mobile applications, and related services (collectively, the "Platform"). By accessing or using our Platform, you agree to be bound by these Terms.',
  },
  {
    slug: 'use-of-services',
    title: 'Use of Services',
    icon: ClipboardList,
    content:
      'Panda On Wheels provides an online marketplace that connects customers with restaurants, chefs, and catering providers for the ordering and delivery of food and related services. You agree to use the Platform only for lawful purposes and in accordance with these Terms.',
  },
  {
    slug: 'accounts',
    title: 'Accounts',
    icon: User,
    content:
      'To access certain features, you may need to create an account. You agree to provide accurate, complete, and up-to-date information and to keep your account credentials secure. You are responsible for all activity that occurs under your account.',
  },
  {
    slug: 'orders-payments',
    title: 'Orders & Payments',
    icon: CreditCard,
    content:
      'Customers can place food orders through the Platform. All prices are listed in the applicable currency and may include taxes (where applicable). Taxes apply to customer orders as required by local regulations. Delivery fees are calculated based on the delivery distance from the restaurant or chef to your address. Payment must be completed through the available payment methods on the Platform.',
  },
  {
    slug: 'delivery-cancellations',
    title: 'Delivery & Cancellations',
    icon: Truck,
    content:
      'Delivery availability, timing, and fees may vary by address, distance, traffic, weather, and other external factors. Estimated delivery times are not guaranteed. Cancellations and refunds are subject to our applicable order policies, which may vary depending on the order status.',
  },
  {
    slug: 'customer-responsibilities',
    title: 'Customer Responsibilities',
    icon: Users,
    content:
      'You agree to provide accurate delivery information, be available to receive your order, and follow any special instructions. You must not misuse the Platform, place fraudulent orders, or engage in any activity that could harm other users, restaurants, or Panda On Wheels.',
  },
  {
    slug: 'restaurant-chef-responsibilities',
    title: 'Restaurant / Chef Responsibilities',
    icon: ChefHat,
    content:
      'Restaurants and chefs are responsible for the quality, safety, and accuracy of the food they prepare and provide, including menu details, ingredient information, allergen disclosure, and compliance with all applicable food safety and legal requirements. By using the Platform, restaurants/chefs agree to our platform terms, including the 30% platform fee on completed orders, as outlined in our pricing terms.',
  },
  {
    slug: 'intellectual-property',
    title: 'Intellectual Property',
    icon: ShieldCheck,
    content:
      'All content on the Platform, including logos, trademarks, text, graphics, and software, is the property of Panda On Wheels or its licensors and may not be used without permission.',
  },
  {
    slug: 'privacy',
    title: 'Privacy',
    icon: Lock,
    content:
      'Your use of the Platform is also governed by our Privacy Policy, which explains how we collect, use, and protect your information.',
  },
  {
    slug: 'limitation-of-liability',
    title: 'Limitation of Liability',
    icon: AlertTriangle,
    content:
      'Panda On Wheels acts as a platform to connect customers with independent restaurants and chefs. We are not responsible for the acts, omissions, or quality of food provided by third-party vendors, except as required by applicable law.',
  },
  {
    slug: 'changes-to-terms',
    title: 'Changes to Terms',
    icon: RotateCcw,
    content:
      'We may update these Terms from time to time. We will notify users of significant changes through the Platform or by other means. Continued use of the Platform after changes become effective constitutes your acceptance of the updated Terms.',
  },
  {
    slug: 'contact-us',
    title: 'Contact Us',
    icon: Mail,
    content:
      'If you have any questions about these Terms & Conditions, please contact our support team through the Contact Us page.',
  },
];

const Terms = () => {
  return (
    <main className="bg-[#FDF6EE] pt-[88px]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <nav className="flex items-center gap-1.5 text-xs text-gray-400">
          <Link to="/" className="hover:text-[#E53935] transition-colors">
            Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-500">Terms &amp; Conditions</span>
        </nav>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-[11px] sm:text-xs font-bold text-[#E53935] uppercase tracking-wider mb-2">
              Terms &amp; Conditions
            </p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#202124] leading-tight">
              Terms &amp; Conditions
            </h1>
            <p className="mt-4 text-gray-500 text-sm sm:text-base max-w-2xl">
              Please read these terms carefully before using Panda On Wheels.
              By accessing or using our platform, you agree to be bound by
              these Terms &amp; Conditions.
            </p>
          </div>
          <div className="lg:col-span-4 hidden lg:flex justify-end">
            <img
              src="/assets/img/panda/hero-food-bowl.png"
              alt=""
              className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200/80 p-3">
              <nav>
                <ol className="space-y-1">
                  {SECTIONS.map(section => {
                    const Icon = section.icon;
                    return (
                      <li key={section.slug}>
                        <a
                          href={`#${section.slug}`}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-panda-tint/30 hover:text-[#E53935] transition-colors"
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          {section.title}
                        </a>
                      </li>
                    );
                  })}
                </ol>
              </nav>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200/80 p-5">
              <h2 className="font-heading font-bold text-base text-[#202124] mb-1">
                Need Help?
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                If you have any questions about these Terms &amp; Conditions,
                we&apos;re here to help.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E53935] text-white text-sm font-heading font-bold hover:bg-[#d32f2f] transition-colors"
              >
                <Headphones className="w-4 h-4" />
                Contact Support
              </Link>
            </div>
          </div>

          <div className="lg:col-span-8 bg-white rounded-2xl border border-gray-200/80 divide-y divide-gray-100">
            {SECTIONS.map((section, index) => (
              <section
                key={section.slug}
                id={section.slug}
                className="p-6 sm:p-8 scroll-mt-28"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-panda-tint text-[#E53935] font-heading font-bold text-xs flex items-center justify-center">
                    {index + 1}
                  </span>
                  <h2 className="font-heading font-bold text-base sm:text-lg text-[#202124]">
                    {section.title}
                  </h2>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed ml-10">
                  {section.content}
                </p>
              </section>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2 px-6 sm:px-8 py-4 text-xs text-gray-400">
              <span>Last updated: {new Date().toLocaleDateString()}</span>
              <span>Panda On Wheels</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Terms;
